/**
 * 통합 API 캐시 시스템
 * 중복 API 호출을 방지하고 전역적으로 캐시를 관리
 */

// 전역 캐시 저장소
const globalApiCache = new Map();
const pendingRequests = new Map(); // 진행 중인 요청 추적

// 캐시 설정
const CACHE_CONFIG = {
  userinfo: 5 * 60 * 1000,    // 5분
  status: 30 * 1000,          // 30초 (자주 변경됨)
  points: 2 * 60 * 1000,      // 2분
  ranking: 1 * 60 * 1000,     // 1분
  friends: 2 * 60 * 1000,     // 2분
  default: 1 * 60 * 1000      // 기본 1분
};

/**
 * 캐시 키 생성
 * @param {string} endpoint - API 엔드포인트
 * @param {Object} params - 요청 파라미터
 * @returns {string} 캐시 키
 */
const generateCacheKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  return `${endpoint}${sortedParams ? `?${sortedParams}` : ''}`;
};

/**
 * 캐시된 데이터 가져오기
 * @param {string} cacheKey - 캐시 키
 * @param {number} maxAge - 최대 캐시 유지 시간 (ms)
 * @returns {any|null} 캐시된 데이터 또는 null
 */
const getCachedData = (cacheKey, maxAge) => {
  const cached = globalApiCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < maxAge) {
    return cached.data;
  }
  return null;
};

/**
 * 캐시에 데이터 저장
 * @param {string} cacheKey - 캐시 키
 * @param {any} data - 저장할 데이터
 */
const setCachedData = (cacheKey, data) => {
  globalApiCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
};

/**
 * 통합 API 요청 함수 (캐싱 및 중복 방지 포함)
 * @param {string} url - 요청 URL
 * @param {Object} options - fetch 옵션
 * @param {string} cacheType - 캐시 타입 (userinfo, status, points 등)
 * @param {Object} cacheParams - 캐시 키 생성용 파라미터
 * @returns {Promise<any>} API 응답 데이터
 */
export const cachedApiRequest = async (url, options = {}, cacheType = 'default', cacheParams = {}) => {
  const cacheKey = generateCacheKey(`${cacheType}_${url}`, cacheParams);
  const maxAge = CACHE_CONFIG[cacheType] || CACHE_CONFIG.default;

  try {
    // 1. 캐시 확인
    const cachedData = getCachedData(cacheKey, maxAge);
    if (cachedData) {
      return cachedData;
    }

    // 2. 진행 중인 요청 확인 (중복 방지)
    if (pendingRequests.has(cacheKey)) {
      return await pendingRequests.get(cacheKey);
    }

    // 3. 새로운 API 요청
    const requestPromise = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api_key': 'kko-a1z2-xv39-82yh0-dqlo',
        ...options.headers
      },
      ...options
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    });

    // 진행 중인 요청으로 등록
    pendingRequests.set(cacheKey, requestPromise);

    const data = await requestPromise;

    // 요청 완료 후 정리
    pendingRequests.delete(cacheKey);

    // 캐시에 저장
    setCachedData(cacheKey, data);

    return data;

  } catch (error) {
    // 실패한 요청 제거
    pendingRequests.delete(cacheKey);
    console.error('❌ API 요청 실패:', cacheKey, error);
    throw error;
  }
};

/**
 * 사용자 정보 API (통합 캐시)
 * @param {string} userId - 사용자 ID
 * @param {string} token - 사용자 토큰
 * @returns {Promise<Object>} 사용자 데이터
 */
export const getUserInfo = async (userId, token) => {
  return await cachedApiRequest(
    'https://api.kkokk.io/api/user/userinfo',
    {
      body: JSON.stringify({ user_id: userId, user_token: token })
    },
    'userinfo',
    { userId }
  );
};

/**
 * 알람 상태 API (통합 캐시)
 * @param {string} token - 사용자 토큰
 * @returns {Promise<Object>} 알람 상태 데이터
 */
export const getAlarmStatus = async (token) => {
  return await cachedApiRequest(
    'https://api.kkokk.io/api/alarm/status',
    {
      method: 'POST',
      body: JSON.stringify({ token })
    },
    'status',
    { token }
  );
};

/**
 * 친구 상태 API (통합 캐시)
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 친구 상태 데이터
 */
export const getFriendStatus = async (userId) => {
  return await cachedApiRequest(
    `https://api.kkokk.io/api/wellness/status?user_id=${userId}`,
    { method: 'GET' },
    'friends',
    { userId }
  );
};

/**
 * 캐시 무효화
 * @param {string} cacheType - 무효화할 캐시 타입 (선택사항)
 * @param {string} userId - 사용자 ID (선택사항)
 */
export const invalidateCache = (cacheType = null, userId = null) => {
  if (cacheType && userId) {
      // 특정 사용자의 특정 타입 캐시만 무효화
      const pattern = `${cacheType}_`;
      for (const [key] of globalApiCache) {
        if (key.startsWith(pattern) && key.includes(userId)) {
          globalApiCache.delete(key);
        }
      }
    } else if (cacheType) {
      // 특정 타입의 모든 캐시 무효화
      const pattern = `${cacheType}_`;
      for (const [key] of globalApiCache) {
        if (key.startsWith(pattern)) {
          globalApiCache.delete(key);
        }
      }
    } else {
      // 모든 캐시 무효화
      globalApiCache.clear();
    }
};

/**
 * 캐시 상태 확인 (디버깅용)
 */
export const getCacheStats = () => {
  const stats = {
    totalEntries: globalApiCache.size,
    pendingRequests: pendingRequests.size,
    entries: []
  };

  for (const [key, value] of globalApiCache) {
    const age = Date.now() - value.timestamp;
    stats.entries.push({
      key,
      age: Math.round(age / 1000) + 's',
      size: JSON.stringify(value.data).length + ' bytes'
    });
  }

  return stats;
};

// 개발 환경에서 전역 디버깅 함수 제공
if (process.env.NODE_ENV === 'development') {
  window.apiCacheStats = getCacheStats;
  window.invalidateApiCache = invalidateCache;
}

export default {
  cachedApiRequest,
  getUserInfo,
  getAlarmStatus,
  getFriendStatus,
  invalidateCache,
  getCacheStats
};
